import { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { Section } from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import { type PriceProps, Typography } from '@elfsight/widget-ui';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import type { TableProps } from '../blocks';
import { Divider, ProductCard, Scrollable, Table, Total } from '../blocks';
import { useCheckoutColors, useCheckoutI18n } from '../providers';
import { useCheckoutNarrowness } from '../utils';

const COMPONENT_NAME = 'summary';

export interface CheckoutSummaryProps {
  productCard?: {
    label?: string;
    description?: string;
    imageSrc?: string;
  };
  tables?: Omit<TableProps, 'textColor'>[];
  totalLabel?: string;
  totalPrice?: PriceProps;
  withFirstDivider?: boolean;
}

export function CheckoutSummary({
  productCard,
  tables = [],
  totalLabel = 'Total',
  totalPrice,
  withFirstDivider = true
}: CheckoutSummaryProps) {
  const { t } = useCheckoutI18n();
  const { textColor, backgroundColor } = useCheckoutColors();
  const isNarrow = useCheckoutNarrowness();

  const withProductCard =
    !!productCard?.label ||
    !!productCard?.description ||
    !!productCard?.imageSrc;
  const withTables = tables.length > 0;
  const withTotal = !!totalLabel;

  const summaryBackgroundColor = isNarrow
    ? backgroundColor
    : getColorWithAlpha(textColor, 0.05);

  return (
    <Component $backgroundColor={summaryBackgroundColor}>
      <Scrollable>
        <Content $align="flex-start" $gap={20} $isNarrow={isNarrow}>
          <Typography
            textToken="text20Bold"
            color={textColor}
            className={createReadableClass({
              component: COMPONENT_NAME,
              element: 'title'
            })}
          >
            {t('Order Summary')}
          </Typography>

          <Show when={withProductCard}>
            <ProductCard {...productCard} />
          </Show>

          <Show when={withTables}>
            {tables.map((table, index) => (
              <Fragment key={`summary-table-${index}`}>
                <Show when={index > 0 || (withFirstDivider && withProductCard)}>
                  <Divider />
                </Show>
                <Section
                  className={createReadableClass({
                    component: COMPONENT_NAME,
                    element: 'section'
                  })}
                  $withoutTopPadding
                  $withoutBottomPadding
                >
                  <Table {...table} />
                </Section>
              </Fragment>
            ))}
          </Show>
        </Content>
      </Scrollable>

      <Show when={withTotal}>
        <Content
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'total-section'
          })}
          $isNarrow={isNarrow}
          $gap={20}
          $wideBottomPadding={40}
          $withoutTopPadding
        >
          <Divider />
          <Total label={totalLabel} price={totalPrice} />
        </Content>
      </Show>
    </Component>
  );
}

const Component = styled.div.attrs<{
  $backgroundColor: string;
}>({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding-right: 4px;

  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

const Content = styled(Section).attrs<{
  $isNarrow: boolean;
  $wideBottomPadding?: number;
  $withoutTopPadding?: boolean;
}>({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'content'
  })
})`
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  width: 100%;

  ${({ $withoutTopPadding, $isNarrow, $wideBottomPadding = 20 }) => css`
    ${$isNarrow &&
    css`
      margin: 0 auto;
      max-width: 500px;
    `}

    padding: ${$isNarrow
      ? '20px 16px 20px 20px'
      : `40px 36px ${$wideBottomPadding}px 40px`};
    ${$withoutTopPadding && 'padding-top: 0px;'}
  `}
`;
